import type { RoomExtra } from './types';

/**
 * Orders room extras by priority ascending (lower number shown first) within each category.
 * Extras with no priority fall to the end of their category and otherwise keep their existing
 * relative order (Array.prototype.sort is stable). Category blocks themselves stay in their
 * original first-seen order so the storefront category headings are preserved.
 */
export const sortExtrasByPriorityWithinCategory = <
  T extends Pick<RoomExtra, 'priority' | 'category'>
>(
  extras: T[]
): T[] => {
  const categoryOrder: string[] = [];
  const groups = new Map<string, T[]>();

  extras.forEach((extra) => {
    const key = extra.category?.name ?? '';
    const group = groups.get(key);
    if (group) {
      group.push(extra);
    } else {
      groups.set(key, [extra]);
      categoryOrder.push(key);
    }
  });

  return categoryOrder.flatMap((key) =>
    [...(groups.get(key) ?? [])].sort((a, b) => {
      const priorityA = a.priority ?? Infinity;
      const priorityB = b.priority ?? Infinity;
      // Compare for equality before subtracting: two unprioritised extras are both Infinity,
      // and Infinity - Infinity is NaN, which silently breaks Array.sort. Returning 0 keeps
      // their existing relative order as the tie-break.
      if (priorityA === priorityB) {
        return 0;
      }
      return priorityA - priorityB;
    })
  );
};
